import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Receipt, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Office Rent", "Salaries", "Marketing", "Telephony", "Software", "Utilities", "Travel", "Commission Payout", "Other"];

export default function Expenses() {
  const { hasPerm } = useAuth();
  const { items, total, loading, page, setPage, refetch } = useList("/expenses", {});
  const create = async (v) => { await api.post("/expenses", v); refetch(); };
  const del = async (e) => { if (!window.confirm("Delete this expense?")) return; try { await api.delete(`/expenses/${e.id}`); toast.success("Deleted"); refetch(); } catch { toast.error("Delete failed"); } };

  const fields = [
    { name: "category", label: "Category", type: "select", required: true, options: CATS },
    { name: "amount", label: "Amount (₹)", type: "number", required: true },
    { name: "date", label: "Date", type: "date", required: true },
    { name: "payment_mode", label: "Payment Mode", type: "select", options: ["UPI", "Card", "NetBanking", "Cheque", "Cash"] },
    { name: "vendor", label: "Vendor" },
    { name: "reference_number", label: "Reference Number" },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];
  const columns = [
    { key: "expense_id", header: "Expense ID", render: (r) => <span className="font-mono text-xs">{r.expense_id}</span> },
    { key: "date", header: "Date", render: (r) => <span className="text-sm">{fmtDate(r.date)}</span> },
    { key: "category", header: "Category", render: (r) => <span className="font-semibold">{r.category}</span> },
    { key: "description", header: "Description", render: (r) => <span className="text-sm text-muted-foreground">{r.description || "—"}</span> },
    { key: "vendor", header: "Vendor", render: (r) => <span className="text-sm">{r.vendor || "—"}</span> },
    { key: "amount", header: "Amount", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold text-rose-600">{INR(r.amount)}</span> },
    ...(hasPerm("expenses:delete") ? [{ key: "actions", header: "", render: (r) => (
      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid="expense-delete-btn"><Trash2 className="h-4 w-4" /></Button>
    ) }] : []),
  ];
  return (
    <div>
      <PageHeader title="Expenses" subtitle={`${total} expense entries · feed into Net Profit`} icon={Receipt}
        actions={hasPerm("expenses:add") && (
          <FormDialog title="New Expense" fields={fields} onSubmit={create} submitLabel="Add Expense" testId="create-expense-dialog"
            trigger={<Button data-testid="create-expense-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Expense</Button>} />
        )} />
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="expense-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
